import React, { useState, useEffect, useCallback } from 'react';
import { FaHistory, FaMapMarkerAlt, FaClock, FaUser } from 'react-icons/fa';
import { Star, CheckCircle, XCircle, MessageSquare, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../services/fetchWithAuth';

const RideHistory = () => {
  const { logout, refreshUser } = useAuth();
  const [rideHistory, setRideHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, cancelled
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchRideHistory = useCallback(async () => {
    try {
      setLoading(true);
      
      // Verify token exists before making the request
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please log in again.');
        logout();
        return;
      }
      
      const response = await fetchWithAuth(`/api/users/ride-history?page=${currentPage}&filter=${filter}`, {}, logout);
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 404 || response.status === 401) {
          toast.error('Session expired. Please log in again.');
          logout();
          return;
        } else if (response.status === 429) {
          throw new Error('Server is busy. Please wait a moment and try again.');
        }
        throw new Error(data.error || 'Failed to fetch ride history');
      }
      
      // Map backend MatchLog fields to frontend ride fields
      const mappedRides = (data.rides || []).map((ride) => ({
        id: ride.id,
        source: ride.source || ride.fareDetails?.source || '',
        destination: ride.destination || ride.fareDetails?.destination || '',
        rideTime: ride.rideTime || ride.rideDate || '',
        fare: ride.fare || ride.fareDetails?.fare || 'N/A',
        partnerName: ride.partnerName || ride.ratings?.partnerName || '',
        partnerRating: ride.partnerRating || ride.ratings?.partnerRating || '',
        userRating: ride.userRating || ride.ratings?.userRating || '',
        status: ride.status || ride.rideStatus || '',
        rideDate: ride.rideDate || '',
        feedback: ride.feedback || ride.ratings?.feedback || '',
        aiRating: ride.aiRating || ride.ratings?.aiRating || '',
        aiConfidence: ride.aiConfidence || ride.ratings?.aiConfidence || '',
        cancellationReason: ride.cancellationReason || '',
      }));
      
      setRideHistory(mappedRides);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      console.error('Fetch ride history error:', error);
      toast.error(error.message || 'Failed to fetch ride history');
      setRideHistory([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filter, logout, refreshUser]);

  useEffect(() => {
    // Fetch ride history when filter or page changes
    fetchRideHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filter]);

  useEffect(() => {
    // eslint-disable-next-line
  }, [rideHistory.length, loading]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no_show':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      case 'no_show':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    return time || 'N/A';
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating})</span>
      </div>
    );
  };

  const completeRide = async (matchId) => {
    try {
      const response = await fetchWithAuth(`/api/connects/matches/${matchId}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      }, logout);
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Server is busy. Please wait a moment and try again.');
        }
        throw new Error(data.error || 'Failed to complete ride');
      }
      
      toast.success('Ride marked as completed!');
      fetchRideHistory();
    } catch (error) {
      console.error('Complete ride error:', error);
      toast.error(error.message || 'Failed to complete ride');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading ride history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Ride History</h1>
              <p className="text-gray-600 mt-1">View your complete ride history and past connections</p>
            </div>
            <div className="flex items-center space-x-2">
              <FaHistory className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex space-x-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All Rides ({rideHistory.length})
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'completed'
                  ? 'bg-green-100 text-green-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Completed ({rideHistory.filter(ride => ride.status === 'completed').length})
            </button>
            <button
              onClick={() => setFilter('cancelled')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'cancelled'
                  ? 'bg-red-100 text-red-700'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Cancelled ({rideHistory.filter(ride => ride.status === 'cancelled').length})
            </button>
          </div>
        </div>

        {/* Ride History List */}
        <div className="p-6">
          {rideHistory.length === 0 ? (
            <div className="text-center py-12">
              <FaHistory className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Rides Yet</h2>
              <p className="text-gray-600 mb-6">Start sharing rides to see your history here!</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                <div className="flex items-center space-x-2 text-blue-700">
                  <FaMapMarkerAlt className="w-5 h-5" />
                  <span className="font-medium">Your ride history will appear here</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {rideHistory.map((ride) => (
                <div key={ride.id} className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FaMapMarkerAlt className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {ride.source} → {ride.destination}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <div className="flex items-center space-x-1">
                            <FaClock className="w-4 h-4" />
                            <span>{formatTime(ride.rideTime)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <FaUser className="w-4 h-4" />
                            <span>{ride.partnerName || 'Unknown'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ride.status)}`}>
                        {getStatusIcon(ride.status)}
                        <span className="ml-1">{ride.status.charAt(0).toUpperCase() + ride.status.slice(1)}</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {formatDate(ride.rideDate)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-3">
                      <div className="text-sm text-gray-600">Fare</div>
                      <div className="font-semibold text-gray-900">₹{ride.fare || 'N/A'}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <div className="text-sm text-gray-600">Partner Rating</div>
                      <div className="flex items-center">
                        {ride.partnerRating ? (
                          renderStars(ride.partnerRating)
                        ) : (
                          <span className="text-gray-500">No rating</span>
                        )}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <div className="text-sm text-gray-600">Your Rating</div>
                      <div className="flex items-center">
                        {ride.userRating ? (
                          renderStars(ride.userRating)
                        ) : (
                          <span className="text-gray-500">No rating</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Complete Ride Button for rides that are not completed or cancelled */}
                  {ride.status === 'accepted' && (
                    <button
                      onClick={() => completeRide(ride.id)}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded mb-4"
                    >
                      Complete Ride
                    </button>
                  )}

                  {/* Feedback Section */}
                  {ride.status === 'completed' && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <MessageSquare className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Feedback</span>
                        </div>
                        {ride.feedback ? (
                          <span className="text-sm text-green-600 flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Submitted
                          </span>
                        ) : (
                          <span className="text-sm text-orange-600 flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            Pending
                          </span>
                        )}
                      </div>
                      {ride.feedback && (
                        <div className="mt-2 bg-white rounded-lg p-3">
                          <p className="text-sm text-gray-700">{ride.feedback}</p>
                          {ride.aiRating && (
                            <div className="mt-2 flex items-center space-x-2 text-xs text-gray-500">
                              <span>AI Rating: {ride.aiRating}/5</span>
                              <span>•</span>
                              <span>Confidence: {(ride.aiConfidence * 100).toFixed(1)}%</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Cancellation Reason */}
                  {ride.status === 'cancelled' && ride.cancellationReason && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Cancellation Reason:</span> {ride.cancellationReason}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <nav className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 border rounded-md text-sm font-medium ${
                      currentPage === page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RideHistory;